'use client';

import { PauseIcon, PlayIcon } from '@heroicons/react/16/solid';
import { useTranslations } from 'next-intl';
import { type ReactNode } from 'react';

import { Button, HeroIcon } from '@/components/atoms';
import { ContentTypeVariations } from '@/components/organisms';
import { usePlayAll } from '@/lib/hooks/PlayAll/usePlayAll';
import { useBreakpoint } from '@/lib/hooks/util/useBreakpoint';

import type {
  Album,
  NewsItem,
  Playlist,
  Podcast,
  PersonalPlaylist,
  FavoritesForController,
  RecentsForController,
  PlaylistTrack,
} from '@/lib/api';
import type { Chapter } from '@/lib/contexts/media-controller/util';

export interface PlayAllButtonProps {
  id?: string;
  mediaType: ContentTypeVariations;
  rawData?:
    | Album
    | Playlist
    | NewsItem
    | Podcast
    | PersonalPlaylist
    | FavoritesForController
    | RecentsForController
    | { id: string; chapters: Chapter[] }
    | undefined;
  startIndex?: number;
  children?: ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  trackId?: number;
  episodeIndex?: number;
  overriddenTrackList?: PlaylistTrack[];
  'aria-label'?: string;
}

export const PlayAllButton = ({
  id,
  mediaType,
  rawData,
  startIndex = 0,
  children,
  className,
  variant = 'primary',
  size = 'md',
  trackId,
  episodeIndex,
  overriddenTrackList,
  'aria-label': ariaLabel,
}: PlayAllButtonProps) => {
  const t = useTranslations();
  const isDesktop = useBreakpoint('md');
  const {
    isLoading,
    isPlaying,
    isControllingCurrentItem,
    isSameType,
    handleClick,
  } = usePlayAll({
    id,
    mediaType,
    rawData,
    startIndex,
    trackId,
    episodeIndex,
    overriddenTrackList,
  });

  const isPlayingCurrent = isSameType && isControllingCurrentItem && isPlaying;
  const icon = isPlayingCurrent ? PauseIcon : PlayIcon;

  const content = (
    <>
      <HeroIcon icon={icon} className="w-5 h-5" />
      {children &&
        (isDesktop ? children : <span className="sr-only">{children}</span>)}
    </>
  );

  const fallbackLabel = children
    ? undefined
    : t(isPlayingCurrent ? 'FOCAL_PAUSE' : 'FOCAL_PLAY');

  return (
    <Button
      onClick={e => handleClick(e)}
      aria-label={ariaLabel ?? fallbackLabel}
      className={className}
      isLoading={isLoading}
      variant={variant}
      size={size}
    >
      {content}
    </Button>
  );
};
